import React, { useEffect, useMemo, useRef, useState } from 'react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../services/supabaseClient'
import { useNavigate } from 'react-router-dom'

export default function Scanner() {
  const { activeSchoolId, allowedSchools, user, signOut, setActiveSchoolId } = useAuth()
  const navigate = useNavigate()

  const roles = useMemo(() => new Set((allowedSchools || []).map(r => r.role).filter(Boolean)), [allowedSchools])
  const canScan = roles.size === 0 ? true : !roles.has('student')

  const videoRef = useRef(null)
  const codeReaderRef = useRef(null)
  const manualInputRef = useRef(null)

  const [cameraOn, setCameraOn] = useState(false)
  const [eventType, setEventType] = useState('in')
  const [manualCode, setManualCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')
  const [recent, setRecent] = useState([])

  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine))
  const [queueCount, setQueueCount] = useState(0)
  const [queuePanelOpen, setQueuePanelOpen] = useState(false)
  const [queueItems, setQueueItems] = useState([])

  const [featuresLoading, setFeaturesLoading] = useState(true)
  const [scannerEnabled, setScannerEnabled] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!activeSchoolId) return
      setFeaturesLoading(true)
      try {
        const { data: features, error: fErr } = await supabase.rpc('get_school_features', { p_school_id: Number(activeSchoolId) })
        if (fErr) throw fErr
        const raw = features ? features.attendance_scanner : undefined
        const enabled = raw === true || String(raw).toLowerCase() === 'true' || String(raw) === '1'
        if (!cancelled) setScannerEnabled(!!enabled)
      } catch (e) {
        console.error('failed to load school features', e)
        // Fail-closed: block scanning if we cannot confirm the feature is enabled.
        if (!cancelled) setScannerEnabled(false)
      } finally {
        if (!cancelled) setFeaturesLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [activeSchoolId])

  const eventTypeRef = useRef(eventType)
  const busyRef = useRef(busy)
  const lastCameraScanRef = useRef({ code: null, at: 0 })
  const queueSyncingRef = useRef(false)

  useEffect(() => { eventTypeRef.current = eventType }, [eventType])
  useEffect(() => { busyRef.current = busy }, [busy])

  useEffect(() => {
    function onOnline() { setOnline(true) }
    function onOffline() { setOnline(false) }
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        if (manualInputRef.current) manualInputRef.current.focus()
      } catch {}
    }, 0)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    return () => {
      try {
        if (codeReaderRef.current) {
          codeReaderRef.current.reset()
        }
      } catch {}
    }
  }, [])

  async function appendRecent(entry) {
    setRecent(prev => [entry, ...prev].slice(0, 20))
  }

  function queueDbOpen() {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open('gate_scanner_db', 1)
      req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains('scan_queue')) {
          db.createObjectStore('scan_queue', { keyPath: 'id', autoIncrement: true })
        }
      }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  }

  async function queueTrimMax(maxItems) {
    try {
      const db = await queueDbOpen()
      const count = await new Promise((resolve, reject) => {
        const tx = db.transaction('scan_queue', 'readonly')
        const store = tx.objectStore('scan_queue')
        const req = store.count()
        req.onsuccess = () => resolve(Number(req.result || 0))
        req.onerror = () => reject(req.error)
      })

      const excess = Math.max(0, count - Number(maxItems || 0))
      if (!excess) {
        db.close()
        return
      }

      await new Promise((resolve, reject) => {
        let remaining = excess
        const tx = db.transaction('scan_queue', 'readwrite')
        const store = tx.objectStore('scan_queue')
        const req = store.openCursor()
        req.onsuccess = () => {
          const cursor = req.result
          if (!cursor || remaining <= 0) {
            resolve(null)
            return
          }
          cursor.delete()
          remaining -= 1
          cursor.continue()
        }
        req.onerror = () => reject(req.error)
      })

      db.close()
    } catch (e) {
      console.error('queueTrimMax failed', e)
    }
  }

  async function queueCountRefresh() {
    try {
      const db = await queueDbOpen()
      await new Promise((resolve, reject) => {
        const tx = db.transaction('scan_queue', 'readonly')
        const store = tx.objectStore('scan_queue')
        const req = store.count()
        req.onsuccess = () => { setQueueCount(Number(req.result || 0)); resolve(null) }
        req.onerror = () => reject(req.error)
      })
      db.close()
    } catch {
      // ignore
    }
  }

  async function queueAdd({ code, event_type, source }) {
    try {
      const db = await queueDbOpen()
      await new Promise((resolve, reject) => {
        const tx = db.transaction('scan_queue', 'readwrite')
        const store = tx.objectStore('scan_queue')
        const req = store.add({
          created_at: new Date().toISOString(),
          school_id: Number(activeSchoolId),
          code,
          event_type,
          source,
          device_label: 'gate-web',
        })
        req.onsuccess = () => resolve(null)
        req.onerror = () => reject(req.error)
      })
      db.close()

      await queueTrimMax(5000)
      await queueCountRefresh()
    } catch (e) {
      console.error('queueAdd failed', e)
    }
  }

  async function queueList(limit = 50) {
    const db = await queueDbOpen()
    const items = await new Promise((resolve, reject) => {
      const out = []
      const tx = db.transaction('scan_queue', 'readonly')
      const store = tx.objectStore('scan_queue')
      const req = store.openCursor()
      req.onsuccess = () => {
        const cursor = req.result
        if (!cursor || out.length >= limit) {
          resolve(out)
          return
        }
        out.push({ id: cursor.key, ...cursor.value })
        cursor.continue()
      }
      req.onerror = () => reject(req.error)
    })
    db.close()
    return items
  }

  async function queueListNewest(limit = 30) {
    const db = await queueDbOpen()
    const items = await new Promise((resolve, reject) => {
      const out = []
      const tx = db.transaction('scan_queue', 'readonly')
      const store = tx.objectStore('scan_queue')
      const req = store.openCursor(null, 'prev')
      req.onsuccess = () => {
        const cursor = req.result
        if (!cursor || out.length >= limit) {
          resolve(out)
          return
        }
        out.push({ id: cursor.key, ...cursor.value })
        cursor.continue()
      }
      req.onerror = () => reject(req.error)
    })
    db.close()
    return items
  }

  async function queueDelete(id) {
    const db = await queueDbOpen()
    await new Promise((resolve, reject) => {
      const tx = db.transaction('scan_queue', 'readwrite')
      const store = tx.objectStore('scan_queue')
      const req = store.delete(id)
      req.onsuccess = () => resolve(null)
      req.onerror = () => reject(req.error)
    })
    db.close()
  }

  async function queueClearAll() {
    const db = await queueDbOpen()
    await new Promise((resolve, reject) => {
      const tx = db.transaction('scan_queue', 'readwrite')
      const store = tx.objectStore('scan_queue')
      const req = store.clear()
      req.onsuccess = () => resolve(null)
      req.onerror = () => reject(req.error)
    })
    db.close()
    await queueCountRefresh()
  }

  async function refreshQueuePanel() {
    try {
      const items = await queueListNewest(30)
      setQueueItems(items)
    } catch {
      setQueueItems([])
    }
  }

  async function syncQueueOnce() {
    if (!activeSchoolId) return
    if (!navigator.onLine) return
    if (queueSyncingRef.current) return
    if (busyRef.current) return

    queueSyncingRef.current = true
    try {
      const batch = await queueList(50)
      if (!batch.length) {
        await queueCountRefresh()
        return
      }

      let performedBy = null
      try {
        const { data: u } = await supabase.auth.getUser()
        performedBy = u?.user?.id || null
      } catch {}

      for (const item of batch) {
        const code = String(item.code || '').trim()
        const effectiveEventType = item.event_type || 'in'
        try {
          const { data: identity, error: idErr } = await supabase
            .from('attendance_scan_identities')
            .select('id, school_id, entity_type, entity_id, active, code')
            .eq('school_id', Number(activeSchoolId))
            .eq('code', code)
            .eq('active', true)
            .maybeSingle()

          if (idErr) throw idErr
          if (!identity?.id) {
            await appendRecent({ at: new Date().toISOString(), code, status: 'unknown', eventType: effectiveEventType, source: 'offline-sync' })
            await queueDelete(item.id)
            continue
          }

          const { error: insErr } = await supabase
            .from('attendance_scan_events')
            .insert([
              {
                school_id: Number(activeSchoolId),
                identity_id: identity.id,
                event_type: effectiveEventType,
                performed_by_user_id: performedBy,
                source: item.source || 'offline',
                device_label: 'gate-web',
              },
            ])

          if (insErr) {
            if (insErr.code === '23505') {
              await appendRecent({ at: new Date().toISOString(), code, status: 'duplicate', eventType: effectiveEventType, source: 'offline-sync' })
              await queueDelete(item.id)
              continue
            }
            throw insErr
          }

          await appendRecent({ at: new Date().toISOString(), code, status: 'ok', eventType: effectiveEventType, source: 'offline-sync' })
          await queueDelete(item.id)
        } catch (e) {
          const msg = String(e?.message || '')
          if (!navigator.onLine || msg.toLowerCase().includes('failed to fetch') || msg.toLowerCase().includes('network')) {
            break
          }
          await appendRecent({ at: new Date().toISOString(), code, status: 'error', eventType: effectiveEventType, source: 'offline-sync' })
          await queueDelete(item.id)
        }
      }

      await queueCountRefresh()
    } catch (e) {
      console.error('syncQueueOnce failed', e)
    } finally {
      queueSyncingRef.current = false
    }
  }

  useEffect(() => {
    queueCountRefresh()
  }, [activeSchoolId])

  useEffect(() => {
    if (!queuePanelOpen) return
    refreshQueuePanel()
    const t = setInterval(() => refreshQueuePanel(), 2000)
    return () => clearInterval(t)
  }, [queuePanelOpen])

  useEffect(() => {
    if (!online) return
    syncQueueOnce()
    const t = setInterval(() => {
      syncQueueOnce()
    }, 10000)
    return () => clearInterval(t)
  }, [online, activeSchoolId])

  async function submitScan(scannedCode, source = 'manual', eventTypeOverride = null) {
    const code = String(scannedCode || '').trim()
    if (!activeSchoolId) return
    if (!code) return

    const effectiveEventType = eventTypeOverride || eventTypeRef.current || eventType

    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        await queueAdd({ code, event_type: effectiveEventType, source })
        setMsg('تم الحفظ بدون إنترنت وسيتم المزامنة تلقائياً عند توفر الاتصال.')
        await appendRecent({ at: new Date().toISOString(), code, status: 'queued', eventType: effectiveEventType, source })
        return
      }

      setBusy(true)
      setErr('')
      setMsg('')

      const { data: identity, error: idErr } = await supabase
        .from('attendance_scan_identities')
        .select('id, school_id, entity_type, entity_id, active, code')
        .eq('school_id', Number(activeSchoolId))
        .eq('code', code)
        .eq('active', true)
        .maybeSingle()

      if (idErr) throw idErr
      if (!identity?.id) {
        setErr('الكود غير معروف أو غير مفعل.')
        await appendRecent({ at: new Date().toISOString(), code, status: 'unknown', eventType, source })
        return
      }

      let performedBy = null
      try {
        const { data: u } = await supabase.auth.getUser()
        performedBy = u?.user?.id || null
      } catch {}

      const { error: insErr } = await supabase
        .from('attendance_scan_events')
        .insert([
          {
            school_id: Number(activeSchoolId),
            identity_id: identity.id,
            event_type: effectiveEventType,
            performed_by_user_id: performedBy,
            source,
            device_label: 'gate-web',
          },
        ])

      if (insErr) {
        if (insErr.code === '23505') {
          setMsg(effectiveEventType === 'in' ? 'تم تسجيل الدخول مسبقاً اليوم (تجاهل).' : 'تم تسجيل الخروج مسبقاً اليوم (تجاهل).')
          await appendRecent({ at: new Date().toISOString(), code, status: 'duplicate', eventType: effectiveEventType, source })
          return
        }
        const msg = String(insErr?.message || '')
        if (!navigator.onLine || msg.toLowerCase().includes('failed to fetch') || msg.toLowerCase().includes('network')) {
          await queueAdd({ code, event_type: effectiveEventType, source })
          setMsg('تعذر الإرسال بسبب الاتصال. تم الحفظ وسيتم المزامنة تلقائياً.')
          await appendRecent({ at: new Date().toISOString(), code, status: 'queued', eventType: effectiveEventType, source })
          return
        }
        throw insErr
      }

      setMsg(effectiveEventType === 'in' ? 'تم تسجيل الدخول بنجاح.' : 'تم تسجيل الخروج بنجاح.')
      await appendRecent({ at: new Date().toISOString(), code, status: 'ok', eventType: effectiveEventType, source })
    } catch (e) {
      console.error('submitScan failed', e)
      const msg = String(e?.message || '')
      if (!navigator.onLine || msg.toLowerCase().includes('failed to fetch') || msg.toLowerCase().includes('network')) {
        await queueAdd({ code, event_type: effectiveEventType, source })
        setMsg('تعذر الإرسال بسبب الاتصال. تم الحفظ وسيتم المزامنة تلقائياً.')
        await appendRecent({ at: new Date().toISOString(), code, status: 'queued', eventType: effectiveEventType, source })
      } else {
        setErr(e?.message || 'تعذر تسجيل المسح.')
        await appendRecent({ at: new Date().toISOString(), code: String(scannedCode || ''), status: 'error', eventType: effectiveEventType, source })
      }
    } finally {
      setBusy(false)
    }
  }

  async function startCamera() {
    if (!canScan) return
    if (!videoRef.current) return
    if (cameraOn) return

    setErr('')
    setMsg('')

    try {
      const reader = new BrowserMultiFormatReader()
      codeReaderRef.current = reader
      setCameraOn(true)

      await reader.decodeFromVideoDevice(null, videoRef.current, async (result, error) => {
        if (result) {
          const text = result.getText()
          const now = Date.now()
          const last = lastCameraScanRef.current
          const same = last?.code && String(last.code) === String(text) && (now - Number(last.at || 0) < 1500)
          if (same) return
          lastCameraScanRef.current = { code: text, at: now }
          if (!busyRef.current) {
            await submitScan(text, 'camera', eventTypeRef.current)
          }
        }
        if (error) {
          // ignore
        }
      })
    } catch (e) {
      console.error('startCamera failed', e)
      setCameraOn(false)
      setErr('تعذر تشغيل الكاميرا. تأكد من صلاحية الكاميرا (Camera permission).')
    }
  }

  function stopCamera() {
    try {
      if (codeReaderRef.current) {
        codeReaderRef.current.reset()
      }
    } catch {}
    setCameraOn(false)
  }

  if (!canScan) {
    return (
      <div dir="rtl" style={{ padding: 16 }}>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 12, borderRadius: 10 }}>
          لا تملك صلاحية استخدام الماسح.
        </div>
      </div>
    )
  }

  if (featuresLoading) {
    return <div style={{ textAlign: 'center', margin: '2rem' }}>جاري التحميل...</div>
  }

  if (!scannerEnabled) {
    return (
      <div dir="rtl" style={{ maxWidth: 680, margin: '2rem auto', padding: 16, display: 'grid', gap: 12 }}>
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', padding: 12, borderRadius: 10 }}>
          هذه الميزة غير مفعلة لهذه المدرسة حالياً.
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            style={btnSecondary}
            onClick={() => {
              setActiveSchoolId(null)
              navigate('/select-school', { replace: true })
            }}
          >
            تغيير المدرسة
          </button>
          <button style={btnSecondary} onClick={signOut}>تسجيل الخروج</button>
        </div>
      </div>
    )
  }

  return (
    <div dir="rtl" style={{ display: 'grid', gap: 12, padding: 16, maxWidth: 920, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: 4 }}>
          <h2 style={{ margin: 0 }}>ماسح الحضور (Gate)</h2>
          {user?.email ? (
            <div style={{ fontSize: 12, color: '#6b7280' }}>{user.email}</div>
          ) : null}
          <div style={{ fontSize: 12, color: online ? '#065f46' : '#9a3412' }}>
            {online ? 'متصل' : 'غير متصل'}{queueCount ? ` • بالانتظار: ${queueCount}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={eventType} onChange={e => setEventType(e.target.value)} style={inputStyle} disabled={busy}>
            <option value="in">دخول (IN)</option>
            <option value="out">خروج (OUT)</option>
          </select>
          <button style={btnSecondary} onClick={syncQueueOnce} disabled={busy || !online || queueCount === 0}>مزامنة</button>
          <button style={btnSecondary} onClick={() => setQueuePanelOpen(v => !v)}>
            {queuePanelOpen ? 'إخفاء الانتظار' : 'قائمة الانتظار'}
          </button>
          {!cameraOn ? (
            <button style={btnPrimary} onClick={startCamera} disabled={busy}>تشغيل الكاميرا</button>
          ) : (
            <button style={btnSecondary} onClick={stopCamera}>إيقاف الكاميرا</button>
          )}
          <button style={btnSecondary} onClick={signOut}>تسجيل الخروج</button>
        </div>
      </div>

      {err ? (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 10, borderRadius: 10 }}>
          {err}
        </div>
      ) : null}
      {msg ? (
        <div style={{ background: '#ecfdf5', border: '1px solid #d1fae5', color: '#065f46', padding: 10, borderRadius: 10 }}>
          {msg}
        </div>
      ) : null}

      {queuePanelOpen ? (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff' }}>
          <div style={{ padding: 10, borderBottom: '1px solid #e5e7eb', fontWeight: 700, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div>قائمة الانتظار (آخر 30) {queueCount ? `• الإجمالي: ${queueCount}` : ''}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button style={btnSecondary} onClick={refreshQueuePanel}>تحديث</button>
              <button style={btnSecondary} onClick={queueClearAll} disabled={queueCount === 0}>مسح الكل</button>
            </div>
          </div>
          <div style={{ padding: 10, display: 'grid', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
            {queueItems.length === 0 ? (
              <div style={{ color: '#6b7280' }}>لا يوجد عناصر في الانتظار.</div>
            ) : (
              queueItems.map(item => (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', border: '1px solid #f3f4f6', padding: 8, borderRadius: 10 }}>
                  <div style={{ display: 'grid', gap: 2, minWidth: 200 }}>
                    <div style={{ fontWeight: 700 }}>{item.event_type || 'in'} • {item.source || 'offline'}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{item.created_at || ''}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', textAlign: 'left', wordBreak: 'break-all', flex: 1 }}>
                    {item.code}
                  </div>
                  <button
                    style={btnSecondary}
                    onClick={async () => {
                      await queueDelete(item.id)
                      await queueCountRefresh()
                      await refreshQueuePanel()
                    }}
                  >
                    حذف
                  </button>
                </div>
              ))
            )}
          </div>
          <div style={{ padding: 10, borderTop: '1px solid #e5e7eb', fontSize: 12, color: '#6b7280' }}>
            يتم حفظ العناصر محلياً عند عدم توفر الإنترنت. يوجد حد أقصى 5000 عنصر (يتم حذف الأقدم تلقائياً).
          </div>
        </div>
      ) : null}

      <div style={{ display: 'grid', gap: 10 }}>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
          <div style={{ padding: 10, borderBottom: '1px solid #e5e7eb', fontWeight: 700 }}>الكاميرا</div>
          <div style={{ padding: 10 }}>
            <video ref={videoRef} style={{ width: '100%', maxHeight: 380, background: '#111827', borderRadius: 10 }}>
              <track kind="captions" />
            </video>
            <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
              ملاحظة: إذا لم تعمل الكاميرا، يمكنك استخدام الإدخال اليدوي أدناه.
            </div>
          </div>
        </div>

        <form
          onSubmit={async (e) => {
            e.preventDefault()
            await submitScan(manualCode, 'manual', eventType)
            setManualCode('')
            try {
              if (manualInputRef.current) manualInputRef.current.focus()
            } catch {}
          }}
          style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', padding: 12 }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>إدخال يدوي</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              ref={manualInputRef}
              style={{ ...inputStyle, flex: 1, minWidth: 240 }}
              value={manualCode}
              onChange={async e => {
                const raw = e.target.value
                if (raw.includes('\n') || raw.includes('\r')) {
                  const cleaned = String(raw).replace(/\r|\n/g, '').trim()
                  setManualCode(cleaned)
                  if (cleaned) {
                    await submitScan(cleaned, 'manual', eventType)
                    setManualCode('')
                    try {
                      if (manualInputRef.current) manualInputRef.current.focus()
                    } catch {}
                  }
                  return
                }
                setManualCode(raw)
              }}
              placeholder="الصق/اكتب كود UUID..."
              disabled={busy}
            />
            <button style={btnPrimary} type="submit" disabled={busy || !manualCode.trim()}>
              {busy ? '...' : 'تسجيل'}
            </button>
          </div>
        </form>

        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff' }}>
          <div style={{ padding: 10, borderBottom: '1px solid #e5e7eb', fontWeight: 700 }}>آخر العمليات</div>
          <div style={{ padding: 10, display: 'grid', gap: 6 }}>
            {recent.length === 0 ? (
              <div style={{ color: '#6b7280' }}>لا يوجد عمليات بعد.</div>
            ) : (
              recent.map((r, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, border: '1px solid #f3f4f6', padding: 8, borderRadius: 10 }}>
                  <div style={{ display: 'grid', gap: 2 }}>
                    <div style={{ fontWeight: 700 }}>{r.status}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{r.eventType} • {r.source}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', textAlign: 'left', wordBreak: 'break-all' }}>
                    {r.code}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const inputStyle = {
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#111827',
}

const btnPrimary = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #1d4ed8',
  background: '#2563eb',
  color: '#fff',
  cursor: 'pointer',
}

const btnSecondary = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#374151',
  cursor: 'pointer',
}
